import React from 'react';
import styled from 'styled-components';
import { trackEvent } from '@99designs/tracking';
import Attribution from '../../../../shared/components/Attribution';
import Heading from '../../../../shared/components/Heading';
import Image from '../../../../shared/components/Image';
import { FAQSchema } from '../../../../shared/components/Schema';
import SearchableAccordion from '../../../../shared/components/SearchableAccordion';
import { useSite } from '../../../../shared/lib/site';
import { colors, media, spaces } from '../../../../shared/lib/styles';
import Box from '../../../../shared/spa/core-components/Box';
import { publicImageUrl } from '../../../lib/publicImageUrl';
import { getFAQs, getSchemaFAQs } from './FAQs';
import type { Currency } from '../../../../shared/lib/currency';

const featureColor = '#ff4691';

const FAQWrapper = styled(Box)`
    color: ${featureColor};
    max-width: 604px;
    padding: ${spaces.space14}px;
    padding-bottom: 0;
    background: linear-gradient(to top, ${colors.white}, ${colors.grey100} 100%, transparent 75%);

    @media ${media('small')} {
        background: none;
        padding: 0;
    }

    @media ${media('small', 'medium')} {
        padding-bottom: ${spaces.space14}px;
    }
`;

const ImgAttributionWrapper = styled(Box)`
    position: relative;
    @media ${media('small')} {
        margin-bottom: ${spaces.space12}px;
    }
`;

const AttributionWrapper = styled.div`
    display: block;
    position: absolute;
    top: 80%;
    transform: translateX(420%);

    @media ${media('small')} {
        transform: translateX(251%);
        margin-top: ${spaces.space6}px;
        top: 100%;
    }
`;

const ImageWrapper = styled.span`
    position: relative;
    left: 6px;

    @media ${media('small')} {
        left: 0;
    }
`;

interface Props {
    questions: Question[];
    description: string;
    categoryKey: string;
}

export interface Question {
    id: string;
    title: string;
    keywords: string[];
    messages: Message[];
}

export interface Message {
    copy: string;
    links: Link;
}

interface Link {
    [index: string]: string;
}

const FAQSection = ({ categoryKey, questions, description }: Props) => {
    const site = useSite();
    const currency = site.siteConfig.currency;
    const faqs = getFAQs(questions, currency as Currency);
    const schemaFaqs = getSchemaFAQs(questions);

    return (
        <>
            <ImgAttributionWrapper>
                <ImageWrapper>
                    <Image
                        src={publicImageUrl('support/rainbow.png')}
                        alt={__(
                            "illustration by E·the·re·al'' of a cartoon person's head with a bright rainbow over their brain"
                        )}
                        width={370}
                        height={252}
                    />
                </ImageWrapper>
                <AttributionWrapper>
                    <Attribution name="E·the·re·al''" />
                </AttributionWrapper>
            </ImgAttributionWrapper>

            <FAQWrapper>
                <Heading type="h2" variations={['h2', 'h1-capped', 'with-divider']}>
                    {description}
                </Heading>
                <FAQSchema faqs={schemaFaqs} />
                <SearchableAccordion
                    items={faqs}
                    contactSectionId="contact"
                    onItemOpen={({ id, title }) =>
                        trackEvent('Clicked FAQ on Product Page', {
                            category: categoryKey,
                            id,
                            title,
                        })
                    }
                    onSearch={({ searchQuery, totalSearchResults }) => {
                        trackEvent('Performed Search', {
                            location: 'product-page',
                            category: categoryKey,
                            searchTerm: searchQuery,
                            searchTarget: 'faqs',
                            noOfSearchResults: totalSearchResults,
                        });
                    }}
                    onPagination={(pageNumber) =>
                        trackEvent('Clicked FAQ Pagination on Product Page', {
                            category: categoryKey,
                            page: pageNumber,
                        })
                    }
                />
            </FAQWrapper>
        </>
    );
};

export default FAQSection;
